import { useState, useEffect, useRef } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useNextgenRouter, TextLink, useTranslation, FAIcon, useSession } from '@nextgen-web-framework/all';
import { StorageService, Logger } from '@nextgen-web-framework/core';
import {
  CartTotal,
  EmptyCart,
  CartMobileHeader,
  CartLoader,
  CartLoaderType,
  ProxyOrderBanner,
  CartLineItems,
  AboMembershipRenewal,
  useFeatureFlag,
} from '@commerce-cart-webcomponents/ui';
import {
  type Cart,
  CartId,
  type InitialCartState,
  getActiveCart,
  getCartByIdReqObj,
  updateEntriesFromCheckoutData,
  ExceptionsEnum,
  Severity,
  getAllExceptionWithSeverity,
  isExceptionExist,
  CartConstantsEnum,
  CartEvents,
  CartEventsEnum,
  CartEventsVersionsEnum,
  CartRoutePaths,
  OrderType,
  userNotRegistered,
  tealiumEventPublisher,
  showEmptyCart,
  getLoaderType,
  getUpdatedEntriesFromModificationArray,
  cartApi,
  updateBlockerException,
  updateCart,
  updateRedirectionToHomePage,
  updateShippingAddress,
  SubCartDetail,
} from '@commerce-cart-webcomponents/sdk';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { ErrorCard, Errors, useAddressUpdate } from '@commerce-webcomponents/ui';
import { CartResponse } from '@commerce-cart-webcomponents/sdk/services/models/CartResponse';
import { Modification } from '@commerce-cart-webcomponents/sdk/services/models/Modification';
import { ExceptionModal } from '../exception-modal/ExceptionModal';
import { RecommendedProducts } from '../recommended-products/RecommendedProducts';
import { errorImagePath, ExceptionTitleData } from './types/enums';

import Styles from './CartContainer.module.scss';

export type CartContainerProps = {
  checkoutHandler: () => void;
  isMainCart?: boolean;
  isSubCart?: boolean;
  displayCheckoutButton?: boolean;
  cartDetailsPage?: boolean;
};

export const CartContainer = ({
  checkoutHandler,
  isSubCart = false,
  displayCheckoutButton = true,
  isMainCart = true,
  cartDetailsPage = false,
}: CartContainerProps) => {
  const t = useTranslation();
  const configs = ConfigService.getConfigs();
  const assetsBasePath = configs.NEXT_ASSETS_BASE_PATH as string;
  const { isABO } = useSession();
  const router = useNextgenRouter();
  const subscriptionIds = useRef<string[]>([]);
  const [cartId, setCartId] = useState(router.params?.cartId ?? '');
  const [exceptionModal, setExceptionModal] = useState(false);
  const [isToggled, setIsToggled] = useState(false);
  const [gcoFlagLS, setGCOFlagLS] = useState<boolean>(false);
  const [exceptionTitleData, setExceptionTitleData] = useState<ExceptionTitleData>({
    title: '',
    description: '',
  });
  const { cart, isLoading, cartCheckoutData } = useSelector(
    (state: { commerceCart: InitialCartState }) => state.commerceCart,
    shallowEqual,
  );
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { isBlockerException, redirectToHomePage }: InitialCartState = commerceCartState;

  let activeCart = getActiveCart(cart, (cartId as string) ?? cart.id);
  const [cartData, setCartData] = useState<Cart>({ ...cart });
  const [showErrorCard, setShowErrorCard] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [isApiInProgress, setIsApiInProgress] = useState(false);
  const loggerInstance = Logger.getInstance();

  const productExceptionDescriptionText = t('cart.cartContainer.productExecptionDescription');
  const productExceptionTitleText = t('cart.cartContainer.productExecptionTitle');
  const promotionExceptionTitleText = t('cart.cartContainer.promotionExecptionTitle');
  const promotionExceptionDescriptionText = t('cart.cartContainer.promotionExecptionDescription');
  const notRegistered = userNotRegistered(cartData);
  const { updateAddress } = useAddressUpdate();
  const isRegularCart = cart.orderType === OrderType.Regular;
  const continueText = t('cart.cartContainer.continueText');
  const localstorage = StorageService.getLocalStorage();
  const { showRecommendedProductsFlag } = useFeatureFlag();

  const clickedShowCart = (id: string) => {
    const cartReq = { ...getCartByIdReqObj };
    cartReq.url = cartReq.url.replace(CartId, id);
    router.push && router.push(cartReq.url.replace(/[#]/g, encodeURIComponent('#')));
  };

  const backToCartList = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    router.back();
  };

  const showBackToCartList = (empty = false) => (
    <>
      {isSubCart && (
        <div className={`${empty ? Styles['back-To-CartContainer-empty'] : Styles['back-To-CartContainer']}`}>
          <FAIcon iconName="chevron-left" onClick={backToCartList} />
          <TextLink href="" text={t('cart.cartContainer.backToCartList')} onClick={backToCartList} />
        </div>
      )}
    </>
  );

  const updateCartEntries = (activeSubCartId?: string, payloadData?: Modification[]) => {
    const allEntries = getUpdatedEntriesFromModificationArray(payloadData);
    const payload = {
      subCarts: [
        {
          subCartId: activeSubCartId,
          entries: allEntries,
        },
      ],
      version: Number(localstorage.retrieve(CartConstantsEnum.CartReqVersion)),
    };
    setIsApiInProgress(true);
    cartApi
      .updateCartEntries({
        cartId: localstorage.retrieve(CartConstantsEnum.CartId),
        payload,
      })
      .then((res) => {
        dispatch(updateBlockerException(false));
        dispatch(updateCart(res as CartResponse));
        CartEvents.publishEvent({
          event: CartEventsEnum.UpdateCartEntryQty,
          payload: {
            cart: res,
            cartId: localstorage.retrieve(CartConstantsEnum.CartId),
          },
        });
      })
      .catch(() => dispatch(updateBlockerException(true)))
      .finally(() => {
        loggerInstance.log(' On Finally Block', isApiInProgress);
        setIsApiInProgress(false);
      });
  };

  useEffect(() => {
    setCartId((router.params?.cartId as string) ?? '');
  }, [router.params]);

  useEffect(() => {
    activeCart = getActiveCart(cart, (cartId as string) ?? cart.id);
  }, [cartId]);

  useEffect(() => {
    const gcoFlag = StorageService.getLocalStorage().retrieve(CartConstantsEnum.IsGroupCart);

    setGCOFlagLS(!!gcoFlag);
    if (isLoading) {
      return;
    }

    const blockerExceptions = getAllExceptionWithSeverity(
      [ExceptionsEnum.Exceptions.ProductService],
      Severity.Error,
      cart.errors ?? [],
    );
    if (blockerExceptions.length > 0) {
      setShowErrorCard(true);
    }

    if (isExceptionExist(ExceptionsEnum.Exceptions.ProductService, cart.errors ?? [])) {
      setExceptionTitleData({
        description: productExceptionDescriptionText,
        title: productExceptionTitleText,
      });
      setExceptionModal(true);
    }

    if (isExceptionExist(ExceptionsEnum.Exceptions.PromotionService, cart.errors ?? [])) {
      setExceptionTitleData({
        description: promotionExceptionDescriptionText,
        title: promotionExceptionTitleText,
      });
      setExceptionModal(true);
    }

    const updatedInventoryData = updateEntriesFromCheckoutData(cartCheckoutData, cart);
    setCartData(updatedInventoryData);
  }, [cart, isLoading]);

  useEffect(() => {
    const toggleEventStart = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.IsGCOToggledStart],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setIsToggled(true);
      },
    });

    const toggleEventEnd = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.IsGCOToggledEnd],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setIsToggled(false);
      },
    });

    subscriptionIds.current = [...toggleEventEnd, ...toggleEventStart];

    return () => {
      CartEvents.unsubscribe(subscriptionIds.current);
    };
  }, []);

  useEffect(() => {
    loggerInstance.log('inside useEffect', isApiInProgress);

    if (
      !isApiInProgress &&
      activeCart?.modificationResult &&
      activeCart?.modificationResult?.isDbUpdateRequired &&
      activeCart.modificationResult?.modifications?.length
    ) {
      updateCartEntries(activeCart?.subCartId, activeCart?.modificationResult?.modifications);
    }
    //in case of GCO we will match owner id with all subcarts and update address with parent subcart

    const amwayUserIdinCheckoutLS = localstorage.retrieve('checkoutProfile') as unknown as {
      fulfillmentType: {
        serviceLevel: {
          deliveryAddress: {
            ownerId?: string;
          };
        };
      };
    };

    const ownerId = amwayUserIdinCheckoutLS?.fulfillmentType?.serviceLevel?.deliveryAddress?.ownerId;
    const subcartsOwnerIdNotMatched = cart.subCarts.every(
      (subCart: SubCartDetail) => subCart?.amwayUser?.amwayUserId !== ownerId,
    );

    if (activeCart && subcartsOwnerIdNotMatched && ownerId !== cart?.amwayUser?.amwayUserId) {
      updateShippingAddress(
        activeCart?.amwayUser?.amwayUserId as string,
        activeCart?.amwayUser?.accountId as string,
        updateAddress,
      );
    }
  }, [cart]);

  const handleBackEventToHomePage = () => {
    if (router?.push) {
      router.push(CartRoutePaths.HOME);
    }
  };

  useEffect(() => {
    if (redirectToHomePage) {
      router.events?.setIsBackBlocked(true);

      router.events?.on('browserBackWarn', () => handleBackEventToHomePage());
      window.history.pushState(null, document.title, window.location.href);
    }
    return () => {
      if (redirectToHomePage) {
        router.events?.setIsBackBlocked(false);
        router.events?.off('browserBackWarn', () => handleBackEventToHomePage());
        window.history.replaceState(null, document.title, window.location.href);
        dispatch(updateRedirectionToHomePage(false));
      }
    };
  }, []);

  if (isLoading) {
    return <CartLoader loaderType={getLoaderType(cart, isToggled, gcoFlagLS, CartLoaderType) as CartLoaderType} />;
  }

  if (showErrorCard || isBlockerException) {
    return (
      <ErrorCard
        title={t('cart.cartContainer.errorTitle')}
        description={t('cart.cartContainer.errorDescription')}
        buttonLabel={t('cart.cartContainer.backToHomeCTA')}
        imageUrl={`${assetsBasePath}/${errorImagePath}`}
        onActionClick={() => {
          if (router?.push) {
            router.push(CartRoutePaths.HOME);
          }
        }}
        type={Errors.General}
      />
    );
  }

  if (
    showEmptyCart({
      cart,
      activeCart,
      cartId: cartId as string,
      isLoading,
    })
  ) {
    return (
      <>
        {showBackToCartList(true)}
        <div className={Styles['commerce-cart-desktop-header']}>
          <div className={Styles['commerce-cart-desktop-header-details']}>
            <CartMobileHeader />
          </div>
          {isRegularCart && <AboMembershipRenewal hasAccountProcess={!!cartData.accountProcess} />}
          {isABO() && !cartId && <ProxyOrderBanner />}
        </div>

        <EmptyCart />
        {showRecommendedProductsFlag && <RecommendedProducts />}
      </>
    );
  }

  return (
    <div className={Styles['cart-container']}>
      <div className={Styles['cart-items']}>
        {exceptionModal && (
          <ExceptionModal
            onButtonClick={() => setExceptionModal(false)}
            buttonTitle={continueText}
            title={exceptionTitleData.title}
            description={exceptionTitleData.description}
          />
        )}
        <div className={Styles['commerce-cart-item-line-page']}>
          {showBackToCartList()}
          <CartLineItems
            data={cartData?.subCarts.length === 0 ? cart : (cartData as Cart)}
            displayGCO={isSubCart ? false : cart.orderType === OrderType.Gco}
            clickedShowCart={clickedShowCart}
            isMainUserCart={isSubCart ? isMainCart : true}
            cartDetailsPage={cartDetailsPage}
          />
        </div>
        <div className={Styles['commerce-cart-items-total-page']}>
          <CartTotal
            data={cart}
            clickCheckoutHandler={() =>
              tealiumEventPublisher(cart, CartEventsEnum.ProceedToCheckout, () => {
                notRegistered ? router.push(CartRoutePaths.REGISTRATION_CHECKOUT) : checkoutHandler();
              })
            }
            displayGCO={isSubCart ? false : cart.orderType === OrderType.Gco}
            displayCheckoutBtn={displayCheckoutButton}
            displayPVBV
          />
        </div>
      </div>
      {showRecommendedProductsFlag && <RecommendedProducts />}
    </div>
  );
};
